import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import CommentSection from "@/components/feature/CommentSection";
import CTASection from "@/components/feature/CTASection";
import ArticleCard from "@/components/base/ArticleCard";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { getArticleById, articles } from "@/mocks/articles";
import { getCommentsByArticleId } from "@/mocks/comments";
import { getCategoryBySlug } from "@/mocks/categories";
import NotFound from "@/pages/NotFound";

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const article = getArticleById(id || "");
  if (!article) {
    return <NotFound />;
  }

  const articleComments = getCommentsByArticleId(article.id);
  const category = getCategoryBySlug(article.categorySlug);
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishDate,
    author: {
      "@type": "Person",
      name: article.author,
      jobTitle: article.authorTitle,
      image: article.authorAvatar,
    },
    publisher: {
      "@type": "Organization",
      name: "51吃瓜网",
      url: "http://9mzg.cn/",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `http://9mzg.cn/article/${article.id}`,
    },
    keywords: article.tags.join(", "),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "http://9mzg.cn/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: article.category,
        item: `http://9mzg.cn/category/${article.categorySlug}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
      },
    ],
  };

  const formatReadCount = (count: number) =>
    count >= 10000 ? `${(count / 10000).toFixed(1)}万` : count;

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <header className="relative h-[500px] md:h-[600px] flex items-center overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={article.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>

          <div className="relative z-10 w-full h-full flex flex-col">
            <div className="flex-1 flex items-center">
              <div className="max-w-7xl mx-auto px-6 md:px-10 w-full">
                <div className="max-w-3xl">
                  <h1 className="font-serif font-black text-3xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
                    {article.title}
                  </h1>
                  <button className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm text-white border border-white/30 hover:bg-white/20 transition-all duration-200 cursor-pointer text-sm">
                    <i className="ri-share-forward-line"></i>
                    {t("article.shareButton")}
                  </button>
                </div>
              </div>
            </div>

            <div className="pb-16 md:pb-24">
              <div className="max-w-7xl mx-auto px-6 md:px-10">
                <p className="font-serif font-bold text-2xl md:text-4xl text-white/90 leading-tight max-w-4xl">
                  {article.excerpt}
                </p>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Breadcrumb
            items={[
              { label: t("common.breadcrumb.home"), href: "/" },
              {
                label: article.category,
                href: `/category/${article.categorySlug}`,
              },
              { label: article.title },
            ]}
          />
        </div>

        {/* Author & Quote Section */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-background-200 text-foreground-600 text-xs font-medium mb-4">
                  {article.isExclusive ? t("article.exclusiveBadge") : article.category}
                </span>
                <h2 className="font-serif font-bold text-2xl md:text-3xl text-foreground-950 leading-snug mb-4">
                  {article.quoteHighlight}
                </h2>
                <p className="text-foreground-500 leading-relaxed">{article.excerpt}</p>
              </div>

              <div className="lg:w-[420px] flex-shrink-0">
                <div className="bg-accent-700 rounded-2xl p-8 text-white relative overflow-hidden">
                  <div className="absolute top-4 left-4">
                    <span className="text-xs text-white/40 uppercase tracking-wider">
                      {t("article.exclusiveBadge")}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4 flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/30"></span>
                    <span className="w-1.5 h-1.5 rounded-full bg-white/30"></span>
                    <span className="w-1.5 h-1.5 rounded-full bg-white/30"></span>
                  </div>
                  <div className="mt-8 mb-6">
                    <p className="text-lg md:text-xl leading-relaxed font-serif">
                      &ldquo;{article.quoteHighlight}&rdquo;
                    </p>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-white/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                        <img
                          src={article.authorAvatar}
                          alt={article.author}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{article.author}</p>
                        <p className="text-xs text-white/60">{article.authorTitle}</p>
                      </div>
                    </div>
                    <button className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors duration-200 cursor-pointer">
                      <i className="ri-arrow-right-up-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <div className="flex flex-wrap items-center gap-4 mb-8 text-sm text-foreground-500">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-200 flex items-center justify-center overflow-hidden">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="font-medium text-foreground-800">{article.author}</span>
              </div>
              <span className="text-foreground-300">|</span>
              <span>
                <i className="ri-eye-line mr-1"></i>
                {formatReadCount(article.readCount)} {t("article.readCount")}
              </span>
              <span className="text-foreground-300">|</span>
              <span>
                <i className="ri-chat-3-line mr-1"></i>
                {formatReadCount(article.commentCount)} {t("article.commentCount")}
              </span>
              <span className="text-foreground-300">|</span>
              <span>{article.publishDate}</span>
            </div>

            <div className="prose prose-lg max-w-none">
              {article.content?.split("\n").map((paragraph, idx) => (
                <p key={idx} className="text-foreground-700 leading-relaxed mb-4 text-base">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200">
              <span className="text-sm text-foreground-500">{t("article.tags")}:</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-200 text-foreground-600 text-xs"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {/* Comments */}
        <CommentSection
          comments={articleComments}
          label={t("article.commentSection.label")}
          title1={t("article.commentSection.title1")}
          title2={t("article.commentSection.title2")}
        />

        {/* Related Articles */}
        <section className="bg-background-100 py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-12">
              <h2 className="font-serif font-bold text-3xl md:text-4xl text-foreground-950 max-w-md leading-tight">
                {t("article.relatedSection.title1")}
                <br />
                {t("article.relatedSection.title2")}
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-sm">
                {t("article.relatedSection.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((related) => (
                <ArticleCard key={related.id} article={related} variant="default" />
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <CTASection
          title={t("common.cta.subscribeTitle")}
          subtitle={t("common.cta.subscribeSubtitle")}
          buttonText={t("common.cta.subscribeButton")}
          variant="default"
        />

        <Footer />
      </div>
    </>
  );
}